import { ApolloClient, InMemoryCache, ApolloLink, CombinedGraphQLErrors, Observable } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import introspection from './gql/introspection';
import runtime from './runtime';
import prepareDataForGraphQL from './utils/prepareDataForGraphQL';
import { getToken, onTokenUpdate, triggerUnauthenticatedError } from './utils/session';
import { checkVersionFromResponse } from './utils/versionCheck';
import wsClient from './wsClient';

// extract the host and protocol from the window location
const { host, protocol } = window.location;

// create the HTTP link for standard GraphQL queries/mutations
const httpLink = new UploadHttpLink({ uri: `${protocol}//${host}/api/graphql`, credentials: 'include' });

const wsLink = new GraphQLWsLink(wsClient);

// link to add the JWT token in authorization header
const addAuthorizationHeaderLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers }) => {
        const token = getToken();

        if (!token) {
            return { headers };
        }

        return {
            headers: {
                ...headers,
                Authorization: `Bearer ${token}`,
                'Apollo-Require-Preflight': 'true',
            },
        };
    });

    return forward(operation);
});

// create a split link to either use HTTTP or WS links based on the definition
const splitLink = ApolloLink.split(
    ({ query }) => {
        const definition = getMainDefinition(query);

        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([addAuthorizationHeaderLink, httpLink])
);

// link to prepare GraphQL variables for submissions
const prepareDataLink = new ApolloLink((operation, forward) => {
    operation.variables = prepareDataForGraphQL(operation.variables);

    return forward(operation);
});

// handle unauthenticated errors
const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
        const isUnauthenticated = error.errors.some(
            ({ extensions }) => extensions?.code === 'UNAUTHENTICATED' || extensions?.code === 'UNAUTHORIZED'
        );

        if (isUnauthenticated) {
            triggerUnauthenticatedError();
        }
    } else if (error) {
        console.error(error);
    }
});

// link to check version from successful responses
const versionCheckLink = new ApolloLink((operation, forward) => {
    return new Observable(observer => {
        const subscription = forward(operation).subscribe({
            next: response => {
                const context = operation.getContext();
                const httpResponse = context.response as Response | undefined;
                checkVersionFromResponse(httpResponse ?? null);
                observer.next(response);
            },
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
        });

        return () => subscription.unsubscribe();
    });
});

const cache = new InMemoryCache({ possibleTypes: introspection.possibleTypes });

// initialize the apollo client
const client = new ApolloClient({
    link: ApolloLink.from([prepareDataLink, versionCheckLink, errorLink, splitLink]),
    cache,
    devtools: { enabled: runtime.apolloDevToolsEnabled },
});

onTokenUpdate(token => {
    if (!token) {
        // once the client is signed out make sure we dropped everything
        // ensure nothing sensitive may remain in the cache
        void client.clearStore();
    }
});

export default client;
